import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import BackButton from "../../components/backButton/BackButton";
import Spinner from "../../components/spinner/Spinner";
import { pedidoBackend } from "../../registro/registro";
import {
  VENTAS_SUSPENDIDAS,
  MENSAJE_VENTAS_SUSPENDIDAS,
} from "../../config/mantenimiento";
import {
  Aviso,
  Badge,
  PanelContainer,
  PanelHeader,
  Tab,
  Tabs,
  Tabla,
  TablaWrapper,
  Vacio,
} from "../panel/panelStyles";
import {
  BotonVenta,
  Buscador,
  ChipConvertida,
  Controles,
  Dato,
  DatosFila,
  Etiqueta,
  MobileVacio,
  SelectorMes,
  SoloDesktop,
  SoloMobile,
  Tarjeta,
  TarjetaHeader,
  ToggleConvertidas,
  TotalLinea,
  VerMas,
} from "./misVentasStyles";

// Mis ventas: el espacio personal del vendedor. Ve SOLO lo suyo (el backend
// filtra por su usuario del token), sin pasar por el panel ni ver datos de
// otros. Dos solapas: ventas y solicitudes. Cada una muestra, por defecto, el
// mes actual con su total (paginado: "Ver más" trae las siguientes); y un
// buscador que rastrea TODO el historial por cliente / DNI / N° de cliente.

const fecha = (iso) =>
  new Date(iso).toLocaleString("es-AR", {
    timeZone: "America/Argentina/Buenos_Aires",
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

// Mes actual (AAAA-MM) en hora de Argentina, para el valor inicial del selector.
const mesActual = () =>
  new Date()
    .toLocaleDateString("en-CA", { timeZone: "America/Argentina/Buenos_Aires" })
    .slice(0, 7);

// "2026-10" → "octubre 2026" (para la línea de total).
const nombreMes = (mes) => {
  const [a, m] = mes.split("-").map(Number);
  return new Date(a, m - 1, 1).toLocaleDateString("es-AR", {
    month: "long",
    year: "numeric",
  });
};

// Dirección en texto a partir de los campos sueltos de la solicitud.
const domicilio = (s) => {
  const calle = [s.calle, s.altura].filter(Boolean).join(" ");
  const base = [calle, s.localidad, s.provincia].filter(Boolean).join(", ");
  if (base && s.referencia) return `${base} (${s.referencia})`;
  return base || s.referencia || "—";
};

// Rebota un valor: evita pegarle al backend en cada tecla del buscador.
function useDebounce(valor, ms) {
  const [v, setV] = useState(valor);
  useEffect(() => {
    const t = setTimeout(() => setV(valor), ms);
    return () => clearTimeout(t);
  }, [valor, ms]);
  return v;
}

// Maneja mes + buscador + paginado contra un endpoint ("/log/mis-ventas" o
// "/log/mis-solicitudes"). La primera página se recarga al cambiar el mes o la
// búsqueda; "Ver más" agrega la siguiente sin recargar lo anterior.
function useMisDatos(endpoint, token, extra = {}) {
  const [mes, setMes] = useState(mesActual);
  const [q, setQ] = useState("");
  const qDebounced = useDebounce(q, 350);
  const buscando = qDebounced.trim().length > 0;

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [cargando, setCargando] = useState(true);
  const [cargandoMas, setCargandoMas] = useState(false);
  const [error, setError] = useState(null);
  const paginaRef = useRef(1);

  // Parámetros extra estables (p. ej. el filtro de convertidas en solicitudes).
  const extraKey = JSON.stringify(extra);
  const paramsDe = (pagina) => ({
    ...extra,
    ...(buscando ? { q: qDebounced.trim(), pagina } : { mes, pagina }),
  });

  // Cambió el alcance (mes o búsqueda): traer la primera página y reemplazar.
  useEffect(() => {
    if (!token) return undefined;
    let vigente = true;
    paginaRef.current = 1;
    setCargando(true);
    setError(null);
    pedidoBackend("get", endpoint, { params: paramsDe(1) })
      .then(({ data }) => {
        if (!vigente) return;
        setTotal(data.total || 0);
        setItems(data.data || []);
      })
      .catch((e) => {
        if (vigente) {
          setError(
            e?.response?.data?.error ||
              "No se pudieron cargar tus datos. Probá de nuevo en un momento.",
          );
        }
      })
      .finally(() => {
        if (vigente) setCargando(false);
      });
    return () => {
      vigente = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, endpoint, mes, qDebounced, extraKey]);

  const verMas = useCallback(() => {
    if (!token || cargandoMas) return;
    const siguiente = paginaRef.current + 1;
    setCargandoMas(true);
    pedidoBackend("get", endpoint, { params: paramsDe(siguiente) })
      .then(({ data }) => {
        paginaRef.current = siguiente;
        setTotal(data.total || 0);
        setItems((prev) => [...prev, ...(data.data || [])]);
      })
      .catch(() => {
        /* si falla "ver más", se queda con lo que ya tenía */
      })
      .finally(() => setCargandoMas(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, endpoint, mes, qDebounced, buscando, cargandoMas, extraKey]);

  return {
    mes,
    setMes,
    q,
    setQ,
    buscando,
    items,
    total,
    cargando,
    cargandoMas,
    error,
    hayMas: items.length < total,
    verMas,
  };
}

const MisVentas = () => {
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.user.currentUser);
  const token = currentUser?.data?.token || false;

  const [tab, setTab] = useState("ventas");
  const [verConvertidas, setVerConvertidas] = useState(false);
  const ventas = useMisDatos("/log/mis-ventas", token);
  const solicitudes = useMisDatos("/log/mis-solicitudes", token, {
    convertidas: verConvertidas ? "ver" : "ocultar",
  });

  // Pasar una solicitud al formulario de ventas con los datos precargados.
  const pasarAVenta = (s) => {
    let telefonos = [];
    try {
      const arr = JSON.parse(s.telefonos || "[]");
      if (Array.isArray(arr)) {
        telefonos = arr
          .map((t) => ({ tipo: t?.tipo || "", numero: t?.numero || "" }))
          .filter((t) => t.numero);
      }
    } catch {
      /* telefonos inválido: se usa el teléfono simple */
    }
    if (!telefonos.length && s.telefono) telefonos = [{ tipo: "", numero: s.telefono }];
    navigate("/formulario", {
      state: {
        desdeSolicitud: {
          origenId: s.origenId,
          prefill: {
            nombre: s.nombre || "",
            apellido: s.apellido || "",
            documento: s.dni || "",
            genero: s.genero || "",
            email: s.email || "",
            telefonos,
            calle: s.calle || "",
            puerta: s.altura || "",
            piso: s.piso || "",
            dpto: s.dpto || "",
            ubicacion: s.referencia || "",
          },
          zona: { provincia: s.provincia, localidad: s.localidad },
        },
      },
    });
  };

  useEffect(() => {
    if (!token) navigate("/");
  }, [token, navigate]);

  // Controles compartidos (buscador + selector de mes) para la solapa activa.
  // Son funciones que devuelven JSX inline —NO componentes <Barra/>— para que el
  // input del buscador no se remonte (y pierda el foco) en cada tecla.
  const barra = (datos, sustantivo) => (
    <>
      <Controles>
        <Buscador
          type="search"
          value={datos.q}
          onChange={(e) => datos.setQ(e.target.value)}
          placeholder="Buscar por cliente, DNI o N° de cliente…"
        />
        {!datos.buscando && (
          <SelectorMes
            type="month"
            value={datos.mes}
            max={mesActual()}
            onChange={(e) => datos.setMes(e.target.value || mesActual())}
          />
        )}
      </Controles>
      {!datos.cargando && !datos.error && (
        <TotalLinea>
          {datos.buscando ? (
            <>
              <strong>{datos.total}</strong>{" "}
              {datos.total === 1 ? "resultado" : "resultados"} para «{datos.q.trim()}» en todo tu historial
            </>
          ) : (
            <>
              <strong style={{ textTransform: "capitalize" }}>{nombreMes(datos.mes)}</strong>{" · "}
              {datos.total} {datos.total === 1 ? sustantivo.singular : sustantivo.plural}
            </>
          )}
        </TotalLinea>
      )}
    </>
  );

  const pieVerMas = (datos) =>
    datos.hayMas ? (
      <VerMas onClick={datos.verMas} disabled={datos.cargandoMas}>
        {datos.cargandoMas ? "Cargando…" : "Ver más"}
      </VerMas>
    ) : null;

  return (
    <PanelContainer>
      <BackButton />
      <PanelHeader>
        <div>
          <h1>Mis ventas</h1>
          <span>
            Lo que cargaste: tus ventas y solicitudes de servicio. Elegí el mes o
            buscá un cliente en todo tu historial.
          </span>
        </div>
      </PanelHeader>

      <Tabs>
        <Tab $activa={tab === "ventas"} onClick={() => setTab("ventas")}>
          Ventas
        </Tab>
        <Tab $activa={tab === "solicitudes"} onClick={() => setTab("solicitudes")}>
          Solicitudes
        </Tab>
      </Tabs>

      {tab === "ventas" ? (
        <>
          {barra(ventas, { singular: "venta", plural: "ventas" })}

          {ventas.cargando ? (
            <div style={{ padding: "2rem", textAlign: "center" }}>
              <Spinner />
            </div>
          ) : ventas.error ? (
            <Aviso $error>{ventas.error}</Aviso>
          ) : (
            <>
              {/* Pantallas grandes: tabla. */}
              <SoloDesktop>
                <TablaWrapper>
                  <Tabla $min="720px">
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Cliente</th>
                        <th>DNI</th>
                        <th>N° cliente</th>
                        <th>Plan</th>
                        <th>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ventas.items.length === 0 && (
                        <tr>
                          <Vacio colSpan={6}>
                            {ventas.buscando
                              ? "No se encontraron ventas con ese dato."
                              : "No cargaste ventas en este mes."}
                          </Vacio>
                        </tr>
                      )}
                      {ventas.items.map((v) => (
                        <tr key={v.id}>
                          <td style={{ whiteSpace: "nowrap" }}>{fecha(v.fechaVenta)}</td>
                          <td>{[v.clienteApellido, v.clienteNombre].filter(Boolean).join(", ") || "—"}</td>
                          <td>{v.clienteDni || "—"}</td>
                          <td>
                            {v.clienteId ? (
                              <strong>{v.clienteId}</strong>
                            ) : (
                              <span style={{ color: "var(--grisMedio)" }}>sin confirmar</span>
                            )}
                          </td>
                          <td title={v.planNombre || ""}>
                            {v.planNombre ? v.planNombre.replace(/^.*? - /, "") : "—"}
                          </td>
                          <td>
                            <Badge $estado={v.estado}>{v.estado}</Badge>
                            {v.respuestaMensaje && v.estado !== "OK" && (
                              <div style={{ color: "var(--grisMedio)", fontSize: "0.75rem", maxWidth: 220 }}>
                                {v.respuestaMensaje}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Tabla>
                </TablaWrapper>
              </SoloDesktop>

              {/* Celular: una tarjeta por venta, sin scroll horizontal. */}
              <SoloMobile>
                {ventas.items.length === 0 ? (
                  <MobileVacio>
                    {ventas.buscando
                      ? "No se encontraron ventas con ese dato."
                      : "No cargaste ventas en este mes."}
                  </MobileVacio>
                ) : (
                  ventas.items.map((v) => (
                    <Tarjeta key={v.id}>
                      <TarjetaHeader>
                        <strong>
                          {[v.clienteApellido, v.clienteNombre].filter(Boolean).join(", ") || "—"}
                        </strong>
                        <time>{fecha(v.fechaVenta)}</time>
                      </TarjetaHeader>
                      <DatosFila>
                        <Dato>
                          <Etiqueta>N° cliente</Etiqueta>
                          {v.clienteId ? (
                            <strong>{v.clienteId}</strong>
                          ) : (
                            <em style={{ color: "var(--grisMedio)", fontStyle: "normal" }}>
                              sin confirmar
                            </em>
                          )}
                        </Dato>
                        <Dato>
                          <Etiqueta>DNI</Etiqueta>
                          {v.clienteDni || "—"}
                        </Dato>
                      </DatosFila>
                      <Dato>
                        <Etiqueta>Plan</Etiqueta>
                        {v.planNombre ? v.planNombre.replace(/^.*? - /, "") : "—"}
                      </Dato>
                      <Dato>
                        <Etiqueta>Estado</Etiqueta>
                        <Badge $estado={v.estado}>{v.estado}</Badge>
                        {v.respuestaMensaje && v.estado !== "OK" && (
                          <div style={{ color: "var(--grisMedio)", fontSize: "0.8rem", marginTop: "0.25rem" }}>
                            {v.respuestaMensaje}
                          </div>
                        )}
                      </Dato>
                    </Tarjeta>
                  ))
                )}
              </SoloMobile>

              {pieVerMas(ventas)}

              <Aviso style={{ marginTop: "0.75rem" }}>
                Si una venta figura como <strong>sin confirmar</strong>, el sistema de Iptel no
                respondió cuando la cargaste: puede haberse creado igual. Antes de volver a
                cargarla, consultá el estado del cliente o avisá a Sistemas.
              </Aviso>
            </>
          )}
        </>
      ) : (
        <>
          {barra(solicitudes, { singular: "solicitud", plural: "solicitudes" })}

          <ToggleConvertidas>
            <input
              type="checkbox"
              checked={verConvertidas}
              onChange={(e) => setVerConvertidas(e.target.checked)}
            />
            Ver también las ya convertidas en venta
          </ToggleConvertidas>

          {solicitudes.cargando ? (
            <div style={{ padding: "2rem", textAlign: "center" }}>
              <Spinner />
            </div>
          ) : solicitudes.error ? (
            <Aviso $error>{solicitudes.error}</Aviso>
          ) : (
            <>
              {/* Pantallas grandes: tabla. */}
              <SoloDesktop>
                <TablaWrapper>
                  <Tabla $min="720px">
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Prospecto</th>
                        <th>DNI</th>
                        <th>Teléfono</th>
                        <th>Domicilio</th>
                        <th> </th>
                      </tr>
                    </thead>
                    <tbody>
                      {solicitudes.items.length === 0 && (
                        <tr>
                          <Vacio colSpan={6}>
                            {solicitudes.buscando
                              ? "No se encontraron solicitudes con ese dato."
                              : "No cargaste solicitudes en este mes."}
                          </Vacio>
                        </tr>
                      )}
                      {solicitudes.items.map((s) => (
                        <tr key={s.id}>
                          <td style={{ whiteSpace: "nowrap" }}>{fecha(s.fechaSolicitud)}</td>
                          <td>{[s.apellido, s.nombre].filter(Boolean).join(", ") || "—"}</td>
                          <td>{s.dni || "—"}</td>
                          <td style={{ whiteSpace: "nowrap" }}>{s.telefono || "—"}</td>
                          <td>{domicilio(s)}</td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            {s.convertidaAt ? (
                              <ChipConvertida>
                                Convertida{s.clienteId ? ` · N° ${s.clienteId}` : ""}
                              </ChipConvertida>
                            ) : (
                              <BotonVenta
                                type="button"
                                onClick={() => pasarAVenta(s)}
                                disabled={VENTAS_SUSPENDIDAS}
                                title={VENTAS_SUSPENDIDAS ? MENSAJE_VENTAS_SUSPENDIDAS : undefined}
                                style={VENTAS_SUSPENDIDAS ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
                              >
                                Pasar a venta
                              </BotonVenta>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Tabla>
                </TablaWrapper>
              </SoloDesktop>

              {/* Celular: una tarjeta por solicitud. */}
              <SoloMobile>
                {solicitudes.items.length === 0 ? (
                  <MobileVacio>
                    {solicitudes.buscando
                      ? "No se encontraron solicitudes con ese dato."
                      : "No cargaste solicitudes en este mes."}
                  </MobileVacio>
                ) : (
                  solicitudes.items.map((s) => (
                    <Tarjeta key={s.id}>
                      <TarjetaHeader>
                        <strong>
                          {[s.apellido, s.nombre].filter(Boolean).join(", ") || "—"}
                        </strong>
                        <time>{fecha(s.fechaSolicitud)}</time>
                      </TarjetaHeader>
                      <DatosFila>
                        <Dato>
                          <Etiqueta>Teléfono</Etiqueta>
                          {s.telefono || "—"}
                        </Dato>
                        <Dato>
                          <Etiqueta>DNI</Etiqueta>
                          {s.dni || "—"}
                        </Dato>
                      </DatosFila>
                      <Dato>
                        <Etiqueta>Domicilio</Etiqueta>
                        {domicilio(s)}
                      </Dato>
                      <div style={{ marginTop: "0.75rem" }}>
                        {s.convertidaAt ? (
                          <ChipConvertida>
                            Convertida{s.clienteId ? ` · N° ${s.clienteId}` : ""}
                          </ChipConvertida>
                        ) : (
                          <BotonVenta
                            type="button"
                            style={{
                              width: "100%",
                              ...(VENTAS_SUSPENDIDAS ? { opacity: 0.5, cursor: "not-allowed" } : {}),
                            }}
                            onClick={() => pasarAVenta(s)}
                            disabled={VENTAS_SUSPENDIDAS}
                            title={VENTAS_SUSPENDIDAS ? MENSAJE_VENTAS_SUSPENDIDAS : undefined}
                          >
                            Pasar a venta
                          </BotonVenta>
                        )}
                      </div>
                    </Tarjeta>
                  ))
                )}
              </SoloMobile>

              {pieVerMas(solicitudes)}
            </>
          )}
        </>
      )}
    </PanelContainer>
  );
};

export default MisVentas;
